import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeOff, ClipboardList, CheckCircle2, TrendingUp, Library } from "lucide-react";

const FONT = { fontFamily: "'Manrope', sans-serif" };

const WORDS = ["Nossal", "Mac.Rob", "Suzanne Cory", "Melbourne High", "JMSS"];

const FEATURES = [
  { icon: ClipboardList, title: "Weekly mocks", body: "Timed, exam-style papers every week." },
  { icon: CheckCircle2, title: "Instant marking", body: "Results the moment you finish." },
  { icon: TrendingUp, title: "Track progress", body: "See which skills to master next." },
  { icon: Library, title: "Resource library", body: "Workbooks, homework and solutions." },
];

const inputCls = "h-12 w-full rounded-xl border-[1.5px] border-[#D6CCF2] bg-white px-4 text-[15px] text-[#1C1240] outline-none transition placeholder:text-[#9b93c0] focus:border-[#4B2BB8] focus:shadow-[0_0_0_4px_rgba(164,133,255,0.25)]";
const labelCls = "mb-1.5 block text-[13px] font-bold text-[#1C1240]";
const primaryBtn = "mt-1 flex h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-[#4B2BB8] text-[16px] font-extrabold text-white transition-colors hover:bg-[#3E22A0] disabled:opacity-70";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | student | tutor
  const [programs, setPrograms] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [tutorDone, setTutorDone] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [stream, setStream] = useState("");

  const panelRef = useRef(null);
  const prefersReduce = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const [wordIdx, setWordIdx] = useState(0);
  const [wordPhase, setWordPhase] = useState("in");
  useEffect(() => {
    const onVis = () => { if (panelRef.current) panelRef.current.classList.toggle("anim-paused", document.hidden); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);
  useEffect(() => {
    if (prefersReduce) return;
    let outT;
    const id = setInterval(() => {
      if (document.hidden) return;
      setWordPhase("out");
      outT = setTimeout(() => { setWordIdx((i) => (i + 1) % WORDS.length); setWordPhase("in"); }, 450);
    }, 3050);
    return () => { clearInterval(id); clearTimeout(outT); };
  }, [prefersReduce]);

  useEffect(() => { api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {}); }, []);

  const reset = (m) => { setMode(m); setError(""); setPassword(""); setShowPwd(false); };
  const forgot = () => toast("Forgot your password? Ask your tutor or the Ember Compass admin to reset it for you.");

  const doSignin = async (e) => {
    e?.preventDefault();
    if (!email || !password) { setError("Please enter your email and password."); return; }
    setBusy(true); setError("");
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
      navigate(u.role === "tutor" ? "/tutor" : "/student");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const doStudent = async (e) => {
    e?.preventDefault();
    if (!stream) { setError("Please choose your entry stream."); return; }
    setBusy(true); setError("");
    try {
      await register({ name: name.trim(), email: email.trim(), password, role: "student", program: stream });
      toast.success("Welcome to Ember Compass");
      navigate("/student");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const doTutor = async (e) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try {
      await register({ name: name.trim(), email: email.trim(), password, role: "tutor" });
      setTutorDone(true);
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const streamOptions = Object.values(programs);
  const eyebrow = mode === "signin" ? "STUDENT SIGN IN" : mode === "student" ? "CREATE ACCOUNT" : "TUTOR APPLICATION";
  const heading = mode === "signin" ? "Welcome back" : mode === "student" ? "Create your account" : "Apply as a tutor";
  const subtitle = mode === "signin" ? "Sign in to your Ember Compass account."
    : mode === "student" ? "Join the portal and start practising this week."
    : "Tutor accounts are reviewed and approved by the administrator before access is granted.";

  const FlameLogo = ({ white = false, className = "" }) => (
    <img src={white ? "/ember-flame-white.png" : "/ember-flame.png"} alt="Ember Education" className={className} />
  );

  return (
    <div className="flex min-h-screen flex-col-reverse lg:flex-row" style={FONT}>
      {/* LEFT — violet brand panel */}
      <div ref={panelRef} className="relative flex flex-1 flex-col justify-between overflow-hidden bg-[#4B2BB8] px-8 py-12 text-white lg:px-[72px] lg:py-14">
        {/* texture: static dot grid + light sweep */}
        <div className="panel-dots" aria-hidden="true" />
        <div className="panel-sweep" aria-hidden="true" />
        {/* decorative flame (static) */}
        <img src="/ember-flame-white.png" alt="" aria-hidden className="pointer-events-none absolute bottom-0 select-none" style={{ height: "800px", width: "auto", right: "-320px", opacity: 0.3, zIndex: 0 }} />

        {/* top row */}
        <div className="relative z-10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FlameLogo white className="h-8 w-auto" />
            <span className="text-[14px] font-extrabold tracking-[0.3em]">EMBER <span className="font-normal text-[#DCD0FF]">EDUCATION</span></span>
          </div>
          <span className="hidden text-[12px] font-bold uppercase tracking-[0.24em] text-[#DCD0FF] sm:block">Year 9 &amp; 10 Entry</span>
        </div>

        {/* headline block */}
        <div className="relative z-10 mt-[90px] max-w-[640px] flex-1">
          <p className="text-[12px] font-bold uppercase tracking-[0.24em] text-[#DCD0FF]">Ember Compass · Student Portal</p>
          <h1 className="mt-5 text-[40px] text-white sm:text-[52px] lg:text-[60px]" style={{ fontWeight: 800, letterSpacing: "-0.035em", lineHeight: 1.02 }}>
            <span className="sr-only">Your portal for Selective Entry &amp; JMSS prep.</span>
            <span aria-hidden="true">Your portal for{" "}
              {prefersReduce ? (
                <span className="text-[#C9B5FF]">Selective Entry &amp; JMSS</span>
              ) : (
                <span className="word-slot">
                  <span className="word-sizer">{WORDS[wordIdx]}</span>
                  <span key={wordIdx} className={`word-anim ${wordPhase}`}>{WORDS[wordIdx]}</span>
                </span>
              )}{" "}prep.
            </span>
          </h1>
          <div className="mt-6 h-[3px] w-16 rounded-full bg-[#A485FF]" />
          <p className="mt-6 max-w-[520px] text-[17px] leading-relaxed text-[#E4DBFF]">
            Weekly mock exams, instant marking and personalised feedback, built by tutors who scored perfectly on both exams.
          </p>
        </div>

        {/* feature row */}
        <div className="relative z-10 mt-12 border-t border-white/[0.22] pt-7">
          <div className="grid grid-cols-2 gap-x-6 gap-y-7 sm:grid-cols-4">
            {FEATURES.map((f) => (
              <div key={f.title}>
                <f.icon className="h-[22px] w-[22px] text-[#C9B5FF]" strokeWidth={2} />
                <div className="mt-3 text-[15px] font-extrabold text-white">{f.title}</div>
                <div className="mt-1 text-[13px] leading-snug text-[#DCD0FF]">{f.body}</div>
              </div>
            ))}
          </div>
          <div className="mt-7 text-[12px] text-[#C9B5FF]">© 2026 Ember Education · Ember Compass</div>
        </div>
      </div>

      {/* RIGHT — white form panel */}
      <div className="flex w-full items-center justify-center bg-white px-6 py-12 lg:w-[560px] lg:shrink-0">
        <div className="w-full max-w-[400px]">
          {/* mobile logo */}
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <FlameLogo className="h-7 w-auto" />
            <span className="text-[13px] font-extrabold tracking-[0.28em] text-[#1C1240]">EMBER <span className="font-normal text-[#6A43E0]">EDUCATION</span></span>
          </div>

          {tutorDone ? (
            <div data-testid="tutor-pending-msg" className="text-center">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-[#F2EEFF] text-[#4B2BB8]"><CheckCircle2 className="h-7 w-7" /></div>
              <h2 className="mt-4 text-[28px] font-extrabold text-[#1C1240]" style={{ letterSpacing: "-0.03em" }}>Application submitted</h2>
              <p className="mt-2 text-[15px] leading-relaxed text-[#5E5389]">Thanks! Your tutor account is awaiting approval by the administrator. You'll be able to sign in as soon as it's approved.</p>
              <button onClick={() => { setTutorDone(false); reset("signin"); }} data-testid="back-to-signin" className={primaryBtn + " mt-6"}>Back to sign in</button>
            </div>
          ) : (
            <>
              {mode !== "tutor" && (
                <div className="mb-7 inline-flex w-full rounded-xl bg-[#F2EEFF] p-1">
                  <button onClick={() => reset("signin")} data-testid="mode-signin"
                    className={`flex-1 rounded-[9px] px-4 py-2 text-[14px] font-bold transition ${mode === "signin" ? "bg-white text-[#4B2BB8] shadow-sm" : "text-[#5E5389]"}`}>Sign in</button>
                  <button onClick={() => reset("student")} data-testid="mode-student"
                    className={`flex-1 rounded-[9px] px-4 py-2 text-[14px] font-bold transition ${mode === "student" ? "bg-white text-[#4B2BB8] shadow-sm" : "text-[#5E5389]"}`}>Create account</button>
                </div>
              )}

              <p className="text-[12px] font-bold uppercase tracking-[0.24em] text-[#6A43E0]">{eyebrow}</p>
              <h2 className="mt-2 text-[34px] font-extrabold text-[#1C1240] sm:text-[36px]" style={{ letterSpacing: "-0.03em", lineHeight: 1.05 }}>{heading}</h2>
              <p className="mt-2 text-[15px] text-[#5E5389]">{subtitle}</p>

              {mode === "signin" && (
                <form onSubmit={doSignin} className="mt-7 space-y-5">
                  <div>
                    <label htmlFor="email" className={labelCls}>Email</label>
                    <input id="email" data-testid="login-email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="student@example.com" className={inputCls} />
                  </div>
                  <div>
                    <div className="mb-1.5 flex items-center justify-between">
                      <label htmlFor="password" className="text-[13px] font-bold text-[#1C1240]">Password</label>
                      <button type="button" onClick={forgot} data-testid="forgot-password-link" className="text-[13px] font-bold text-[#6A43E0] hover:underline">Forgot password?</button>
                    </div>
                    <div className="relative">
                      <input id="password" data-testid="login-password" type={showPwd ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••••" className={inputCls + " pr-12"} />
                      <button type="button" onClick={() => setShowPwd((s) => !s)} aria-label="Toggle password" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5E5389] hover:text-[#4B2BB8]">
                        {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  {error && <p data-testid="login-error" className="text-[14px] font-semibold text-[#E11D48]">{error}</p>}
                  <button type="submit" disabled={busy} data-testid="login-submit" className={primaryBtn}>{busy ? "Signing in…" : <>Sign in <ArrowRight className="h-[18px] w-[18px]" /></>}</button>
                </form>
              )}

              {mode === "student" && (
                <form onSubmit={doStudent} className="mt-7 space-y-5">
                  <div><label htmlFor="name" className={labelCls}>Full name</label><input id="name" data-testid="reg-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jordan Lee" className={inputCls} /></div>
                  <div><label htmlFor="remail" className={labelCls}>Email</label><input id="remail" data-testid="reg-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="student@example.com" className={inputCls} /></div>
                  <div><label htmlFor="rpass" className={labelCls}>Password</label><input id="rpass" data-testid="reg-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className={inputCls} /></div>
                  <div>
                    <label className={labelCls}>Entry stream</label>
                    <Select value={stream} onValueChange={setStream}>
                      <SelectTrigger data-testid="reg-stream" className="h-12 rounded-xl border-[1.5px] border-[#D6CCF2] text-[15px] text-[#1C1240]"><SelectValue placeholder="Choose your stream" /></SelectTrigger>
                      <SelectContent>{streamOptions.map((p) => <SelectItem key={p.key} value={p.key} data-testid={`stream-${p.key}`}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {error && <p data-testid="reg-error" className="text-[14px] font-semibold text-[#E11D48]">{error}</p>}
                  <button type="submit" disabled={busy} data-testid="reg-submit" className={primaryBtn}>{busy ? "Creating account…" : <>Create account <ArrowRight className="h-[18px] w-[18px]" /></>}</button>
                </form>
              )}

              {mode === "tutor" && (
                <form onSubmit={doTutor} className="mt-7 space-y-5">
                  <div><label htmlFor="tname" className={labelCls}>Full name</label><input id="tname" data-testid="tutor-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Alex Morgan" className={inputCls} /></div>
                  <div><label htmlFor="temail" className={labelCls}>Email</label><input id="temail" data-testid="tutor-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={inputCls} /></div>
                  <div><label htmlFor="tpass" className={labelCls}>Password</label><input id="tpass" data-testid="tutor-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className={inputCls} /></div>
                  {error && <p data-testid="tutor-error" className="text-[14px] font-semibold text-[#E11D48]">{error}</p>}
                  <button type="submit" disabled={busy} data-testid="tutor-submit" className={primaryBtn}>{busy ? "Submitting…" : <>Submit application <ArrowRight className="h-[18px] w-[18px]" /></>}</button>
                  <button type="button" onClick={() => reset("signin")} className="w-full text-center text-[14px] font-bold text-[#6A43E0] hover:underline">Back to sign in</button>
                </form>
              )}

              {mode !== "tutor" && (
                <div className="mt-8 flex items-center justify-between border-t border-[#EEE9FB] pt-6">
                  <span className="text-[14px] text-[#5E5389]">Are you a tutor?</span>
                  <button onClick={() => reset("tutor")} data-testid="tutor-apply-link" className="inline-flex items-center gap-1 text-[14px] font-extrabold text-[#4B2BB8] hover:underline">Apply for an account <ArrowRight className="h-[15px] w-[15px]" /></button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
